import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Hash, 
  Copy, 
  Download, 
  Shuffle, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  Code,
  Key,
  Database,
  Zap,
  Grid,
  List,
  Plus,
  Minus,
  Eye,
  EyeOff,
  Info,
  Trash2,
  FileText,
  Globe,
  Cpu,
  Binary
} from 'lucide-react';

interface StringGenerationSettings {
  length: number;
  minLength: number;
  maxLength: number;
  batchSize: number;
  stringType: StringType;
  caseFormat: CaseFormat;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  customCharset: string;
  useCustomCharset: boolean;
  includeSpecialChars: boolean;
  excludeSimilar: boolean;
  usePrefix: boolean;
  prefix: string;
  useSuffix: boolean;
  suffix: string;
  useDelimiters: boolean;
  delimiter: string;
  segmentLength: number;
  encoding: EncodingType;
}

interface GeneratedString {
  id: string;
  value: string;
  type: StringType;
  length: number;
  encoding: EncodingType;
  format: string;
  timestamp: Date;
  uniqueness: number;
  readability: number;
}

interface StringAnalytics {
  totalGenerated: number;
  totalBatches: number;
  averageLength: number;
  typeDistribution: Record<StringType, number>;
  encodingUsage: Record<EncodingType, number>;
  uniquenessScore: number;
  generationHistory: Array<{
    timestamp: Date;
    type: StringType;
    count: number;
    avgLength: number;
  }>;
}

type StringType = 'alphanumeric' | 'alphabetic' | 'numeric' | 'hexadecimal' | 'base64' | 'uuid' | 'apikey' | 'token' | 'custom' | 'binary';
type CaseFormat = 'mixed' | 'uppercase' | 'lowercase' | 'title' | 'camel' | 'snake';
type EncodingType = 'ascii' | 'utf8' | 'hex' | 'base64' | 'url' | 'html';

const RandomStringGenerator: React.FC = () => {
  const [strings, setStrings] = useState<GeneratedString[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [copiedId, setCopiedId] = useState<string>('');
  const [showStrings, setShowStrings] = useState<boolean>(true);

  const [settings, setSettings] = useState<StringGenerationSettings>({
    length: 16,
    minLength: 8,
    maxLength: 64,
    batchSize: 12,
    stringType: 'alphanumeric',
    caseFormat: 'mixed',
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: false,
    customCharset: '',
    useCustomCharset: false,
    includeSpecialChars: false,
    excludeSimilar: false,
    usePrefix: false,
    prefix: '',
    useSuffix: false,
    suffix: '',
    useDelimiters: false,
    delimiter: '-',
    segmentLength: 4,
    encoding: 'ascii'
  });

  const [analytics, setAnalytics] = useState<StringAnalytics>({
    totalGenerated: 0,
    totalBatches: 0,
    averageLength: 0,
    typeDistribution: {
      alphanumeric: 0, alphabetic: 0, numeric: 0, hexadecimal: 0,
      base64: 0, uuid: 0, apikey: 0, token: 0, custom: 0, binary: 0
    },
    encodingUsage: { ascii: 0, utf8: 0, hex: 0, base64: 0, url: 0, html: 0 },
    uniquenessScore: 0,
    generationHistory: []
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "Random String Generator";
    const dynamicTitle = analytics.totalGenerated > 0 
      ? `${baseTitle} - ${analytics.totalGenerated} Random Strings Created`
      : `${baseTitle} - Generate API Keys, Tokens & Custom Strings`;
    
    const description = analytics.totalGenerated > 0
      ? `Professional random string generator with ${analytics.totalGenerated} strings created. Generate API keys, tokens, UUIDs, hex strings, and custom formats. ${analytics.uniquenessScore}% uniqueness score for developers.`
      : "Generate random strings for API keys, tokens, UUIDs, hex codes, and custom formats. Perfect for developers, with bulk generation, multiple encodings, and advanced customization options.";

    const keywords = [
      "random string generator",
      "api key generator", 
      "token generator",
      "uuid generator",
      "hex string generator",
      "base64 generator",
      "alphanumeric generator",
      "random id generator",
      "string generator tool",
      "bulk string generator",
      "custom string generator",
      "developer tools",
      "random code generator",
      "session token generator",
      "authentication token generator"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [analytics.totalGenerated, analytics.uniquenessScore]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Random String Generator",
    "description": seoData.description,
    "url": "https://example.com/random-string-generator",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "API key generation",
      "Token generation",
      "UUID generation",
      "Hexadecimal strings",
      "Base64 encoding",
      "Custom character sets",
      "Bulk generation",
      "Multiple formats",
      "Developer-friendly"
    ],
    "targetAudience": {
      "@type": "Audience",
      "audienceType": "Developers, System Administrators, Software Engineers"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Developer Tools"
    },
    "softwareVersion": "2.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free for personal and commercial development use"
  };

  const characterSets = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
    specialChars: '~`!@#$%^&*()_+-={}[]|\\:";\'<>?,./±§',
    hexChars: '0123456789ABCDEF',
    base64Chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/',
    urlSafeChars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_',
    similar: 'il1Lo0O',
    binary: '01'
  };

  // Enhanced random generation
  const generateSecureRandom = useCallback((max: number): number => {
    if (window.crypto && window.crypto.getRandomValues) {
      const array = new Uint32Array(1);
      const maxUint32 = 0xFFFFFFFF;
      let randomValue;
      
      do {
        window.crypto.getRandomValues(array);
        randomValue = array[0];
      } while (randomValue >= maxUint32 - (maxUint32 % max));
      
      return randomValue % max;
    }
    return Math.floor(Math.random() * max);
  }, []);

  const buildCharacterSet = useCallback((): string => {
    if (settings.useCustomCharset && settings.customCharset) {
      return settings.customCharset;
    }

    let charset = '';
    
    switch (settings.stringType) {
      case 'alphabetic':
        if (settings.includeUppercase) charset += characterSets.uppercase;
        if (settings.includeLowercase) charset += characterSets.lowercase;
        break;
      case 'numeric':
        charset = characterSets.numbers;
        break;
      case 'alphanumeric':
        if (settings.includeUppercase) charset += characterSets.uppercase;
        if (settings.includeLowercase) charset += characterSets.lowercase;
        if (settings.includeNumbers) charset += characterSets.numbers;
        break;
      case 'hexadecimal':
        charset = characterSets.hexChars;
        break;
      case 'base64':
        charset = characterSets.base64Chars;
        break;
      case 'apikey':
        charset = characterSets.urlSafeChars;
        break;
      case 'token':
        charset = characterSets.base64Chars.replace(/[+/]/g, '') + '-_';
        break;
      case 'binary':
        charset = characterSets.binary;
        break;
      case 'custom':
        if (settings.includeUppercase) charset += characterSets.uppercase;
        if (settings.includeLowercase) charset += characterSets.lowercase;
        if (settings.includeNumbers) charset += characterSets.numbers;
        if (settings.includeSymbols) charset += characterSets.symbols;
        if (settings.includeSpecialChars) charset += characterSets.specialChars;
        break;
      default:
        charset = characterSets.lowercase + characterSets.numbers;
    }

    if (settings.excludeSimilar && charset) {
      charset = charset.split('').filter(char => !characterSets.similar.includes(char)).join('');
    }

    return charset || characterSets.lowercase;
  }, [settings]);

  const formatCase = useCallback((str: string): string => {
    switch (settings.caseFormat) {
      case 'uppercase':
        return str.toUpperCase();
      case 'lowercase':
        return str.toLowerCase();
      case 'title':
        return str.replace(/\w\S*/g, (txt) => 
          txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        );
      case 'camel':
        return str.replace(/[-_](.)/g, (_, char) => char.toUpperCase());
      case 'snake':
        return str.replace(/([A-Z])/g, '_$1').toLowerCase();
      case 'mixed':
      default:
        return str;
    }
  }, [settings.caseFormat]);

  const generateUUID = useCallback((): string => {
    const chars = '0123456789abcdef';
    let uuid = '';
    for (let i = 0; i < 36; i++) {
      if (i === 8 || i === 13 || i === 18 || i === 23) {
        uuid += '-';
      } else if (i === 14) {
        uuid += '4';
      } else if (i === 19) {
        uuid += chars[(generateSecureRandom(4) + 8)];
      } else {
        uuid += chars[generateSecureRandom(16)];
      }
    }
    return uuid;
  }, [generateSecureRandom]);

  const applyEncoding = useCallback((str: string): string => {
    switch (settings.encoding) {
      case 'hex':
        return Array.from(str)
          .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
          .join('');
      case 'base64':
        return btoa(str);
      case 'url':
        return encodeURIComponent(str);
      case 'html':
        return str.replace(/[&<>"']/g, (char) => {
          const map: Record<string, string> = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
          };
          return map[char];
        });
      case 'utf8':
      case 'ascii':
      default:
        return str;
    }
  }, [settings.encoding]);

  const calculateUniqueness = useCallback((str: string): number => {
    const uniqueChars = new Set(str).size;
    const maxPossibleUnique = Math.min(str.length, buildCharacterSet().length);
    return Math.round((uniqueChars / maxPossibleUnique) * 100);
  }, [buildCharacterSet]);

  const calculateReadability = useCallback((str: string): number => {
    let score = 100;
    
    // Penalize for similar looking characters
    if (/[il1Lo0O]/.test(str)) score -= 20;
    
    // Penalize for excessive special characters
    const specialCharCount = (str.match(/[^A-Za-z0-9]/g) || []).length;
    if (specialCharCount > str.length * 0.3) score -= 15;
    
    // Penalize for excessive length
    if (str.length > 32) score -= 10;
    
    // Bonus for good structure
    if (settings.useDelimiters) score += 15;
    
    return Math.max(0, score);
  }, [settings.useDelimiters]);

  const generateSingleString = useCallback((): GeneratedString => {
    let result = '';
    
    if (settings.stringType === 'uuid') {
      result = generateUUID();
    } else {
      const charset = buildCharacterSet();
      const length = settings.minLength === settings.maxLength 
        ? settings.length 
        : settings.minLength + generateSecureRandom(settings.maxLength - settings.minLength + 1);
      
      // Generate base string
      for (let i = 0; i < length; i++) {
        result += charset.charAt(generateSecureRandom(charset.length));
      }
      
      // Apply case formatting
      result = formatCase(result);
      
      // Add delimiters
      if (settings.useDelimiters && settings.delimiter && settings.segmentLength > 0) {
        const segments = [];
        for (let i = 0; i < result.length; i += settings.segmentLength) {
          segments.push(result.substring(i, i + settings.segmentLength));
        }
        result = segments.join(settings.delimiter);
      }
    }
    
    // Add prefix/suffix
    if (settings.usePrefix && settings.prefix) {
      result = settings.prefix + result;
    }
    if (settings.useSuffix && settings.suffix) {
      result = result + settings.suffix;
    }
    
    // Apply encoding
    const encodedResult = applyEncoding(result);
    
    const uniqueness = calculateUniqueness(result);
    const readability = calculateReadability(result);
    
    return {
      id: Math.random().toString(36).substr(2, 9),
      value: encodedResult,
      type: settings.stringType,
      length: encodedResult.length,
      encoding: settings.encoding,
      format: `${settings.stringType}-${settings.caseFormat}`,
      timestamp: new Date(),
      uniqueness,
      readability
    };
  }, [settings, generateUUID, buildCharacterSet, generateSecureRandom, formatCase, applyEncoding, calculateUniqueness, calculateReadability]);

  const generateStringBatch = useCallback(async () => {
    setIsGenerating(true);
    const newStrings: GeneratedString[] = [];
    
    // Generate in chunks to avoid blocking UI
    const chunkSize = 4;
    for (let i = 0; i < settings.batchSize; i += chunkSize) {
      const chunk = Math.min(chunkSize, settings.batchSize - i);
      for (let j = 0; j < chunk; j++) {
        newStrings.push(generateSingleString());
      }
      
      // Small delay to keep UI responsive
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    setStrings(prev => [...newStrings, ...prev].slice(0, 1000));
    
    // Update analytics
    setAnalytics(prev => {
      const total = prev.totalGenerated + newStrings.length;
      const avgLength = (prev.averageLength * prev.totalGenerated + 
                        newStrings.reduce((sum, s) => sum + s.length, 0)) / total;
      
      const typeDist = { ...prev.typeDistribution };
      const encodingUsage = { ...prev.encodingUsage };
      
      newStrings.forEach(s => {
        typeDist[s.type]++;
        encodingUsage[s.encoding]++;
      });

      const avgUniqueness = newStrings.reduce((sum, s) => sum + s.uniqueness, 0) / newStrings.length;
      
      const historyEntry = {
        timestamp: new Date(),
        type: settings.stringType,
        count: newStrings.length,
        avgLength: avgLength
      };

      return {
        totalGenerated: total,
        totalBatches: prev.totalBatches + 1,
        averageLength: Math.round(avgLength * 10) / 10,
        typeDistribution: typeDist,
        encodingUsage,
        uniquenessScore: Math.round(avgUniqueness * 10) / 10,
        generationHistory: [historyEntry, ...prev.generationHistory].slice(0, 50)
      };
    });

    setIsGenerating(false);
  }, [settings.batchSize, settings.stringType, generateSingleString]);

  const copyString = async (value: string, id: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy string');
    }
  };

  const copyAllStrings = async () => {
    const allStrings = strings.map(s => s.value).join('\n');
    try {
      await navigator.clipboard.writeText(allStrings);
      setCopiedId('all');
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy strings');
    }
  };

  const exportStrings = () => {
    const data = {
      strings: strings.map(s => ({
        value: s.value,
        type: s.type,
        length: s.length,
        encoding: s.encoding,
        format: s.format,
        uniqueness: s.uniqueness,
        readability: s.readability,
        timestamp: s.timestamp
      })),
      settings,
      analytics,
      exportDate: new Date()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `random-strings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAllStrings = () => {
    setStrings([]);
  };

  const getTypeColor = (type: StringType): string => {
    const colors = {
      alphanumeric: 'bg-blue-500',
      alphabetic: 'bg-green-500',
      numeric: 'bg-yellow-500',
      hexadecimal: 'bg-purple-500',
      base64: 'bg-indigo-500',
      uuid: 'bg-pink-500',
      apikey: 'bg-orange-500',
      token: 'bg-teal-500',
      custom: 'bg-gray-500',
      binary: 'bg-red-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getTypeIcon = (type: StringType) => {
    const icons = {
      alphanumeric: Hash,
      alphabetic: FileText,
      numeric: Binary,
      hexadecimal: Code,
      base64: Database,
      uuid: Globe,
      apikey: Key,
      token: Cpu,
      custom: Settings,
      binary: Binary
    };
    const IconComponent = icons[type] || Hash;
    return <IconComponent className="w-3 h-3" />;
  };

  useEffect(() => {
    generateStringBatch();
  }, []);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-cyan-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Developer Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/random-string-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/random-string-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/random-string-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Random String Generator Tool for Developers" />
        <meta property="og:site_name" content="Developer Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/random-string-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/random-string-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Professional Random String Generator Tool" />
        <meta name="twitter:creator" content="@devtools" />
        <meta name="twitter:site" content="@devtools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#0ea5e9"} />
        <meta name="msapplication-TileColor" content="#0ea5e9" />
        <meta name="application-name" content="String Generator" />
        <meta name="apple-mobile-web-app-title" content="String Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Developer Tool Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Random String Generator",
            "description": "Professional random string generator for developers and system administrators",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web Browser",
            "downloadUrl": "https://example.com/random-string-generator",
            "softwareVersion": "2.0",
            "datePublished": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "Developer Tools"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.7",
              "reviewCount": "1892",
              "bestRating": "5",
              "worstRating": "1"
            },
            "featureList": [
              "API Key Generation",
              "UUID Generation", 
              "Token Generation",
              "Base64 Encoding",
              "Hexadecimal Strings",
              "Custom Character Sets",
              "Bulk Generation",
              "Multiple Formats"
            ]
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What types of random strings can this generate?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our generator supports alphanumeric strings, API keys, UUIDs, tokens, hexadecimal strings, Base64 encoded strings, numeric codes, alphabetic strings, binary strings, and custom formats with configurable character sets."
                }
              },
              {
                "@type": "Question", 
                "name": "Can I generate strings in bulk?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, you can generate up to 50 strings at once. The tool supports batch generation with customizable length ranges and maintains consistent formatting across all generated strings."
                }
              },
              {
                "@type": "Question",
                "name": "What encodings are supported?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The generator supports ASCII, UTF-8, hexadecimal, Base64, URL encoding, and HTML entity encoding. You can apply any encoding to your generated strings for different use cases."
                }
              },
              {
                "@type": "Question",
                "name": "Is this suitable for generating API keys?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely! The tool includes specific modes for API keys and tokens with URL-safe characters, appropriate length defaults, and security-conscious character selection for production use."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Usage Schema */}
        {analytics.totalGenerated > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CreativeWork",
              "name": `${analytics.totalGenerated} Random Strings Generated`,
              "description": `Session statistics: ${analytics.totalBatches} batches, ${analytics.averageLength} average length, ${analytics.uniquenessScore}% uniqueness`,
              "creator": {
                "@type": "SoftwareApplication",
                "name": "Random String Generator"
              },
              "dateCreated": new Date().toISOString(),
              "usageInfo": `Development and system administration purposes`
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <Hash className="w-8 h-8 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    Random String Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generate API keys, tokens, UUIDs, and custom string formats
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* String Type Indicator */}
                <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full">
                  <div className={`w-3 h-3 rounded-full ${getTypeColor(settings.stringType)}`} />
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-300 capitalize">
                    {settings.stringType}
                  </span>
                </div>

                {/* View Mode */}
                <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-white dark:bg-gray-700'} transition-colors`}
                    title="Grid View"
                    aria-label="Switch to grid view"
                  >
                    <Grid className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-white dark:bg-gray-700'} transition-colors`}
                    title="List View"
                    aria-label="Switch to list view"
                  >
                    <List className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                  aria-label="Toggle analytics panel"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                  aria-label="Toggle settings panel"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                  aria-label="Toggle dark/light theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  aria-label={isFullscreen ? "Exit fullscreen mode" : "Enter fullscreen mode"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              {/* Control Panel */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={generateStringBatch}
                      disabled={isGenerating}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                      aria-label={`Generate ${settings.batchSize} random strings`}
                    >
                      {isGenerating ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <Zap className="w-5 h-5" />
                      )}
                      <span>{isGenerating ? 'Generating...' : `Generate ${settings.batchSize}`}</span>
                    </button>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Batch Size:</span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, batchSize: Math.max(1, prev.batchSize - 1) }))}
                          className="p-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                          aria-label="Decrease batch size"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-gray-800 dark:text-white">
                          {settings.batchSize}
                        </span>
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, batchSize: Math.min(50, prev.batchSize + 1) }))}
                          className="p-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                          aria-label="Increase batch size"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowStrings(!showStrings)}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title={showStrings ? "Hide Strings" : "Show Strings"}
                      aria-label={showStrings ? "Hide all strings" : "Show all strings"}
                    >
                      {showStrings ? (
                        <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>

                    <button
                      onClick={copyAllStrings}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Copy All Strings"
                      aria-label="Copy all generated strings"
                    >
                      {copiedId === 'all' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>

                    <button
                      onClick={exportStrings}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Export Strings"
                      aria-label="Export all strings to JSON file"
                    >
                      <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>

                    <button
                      onClick={clearAllStrings}
                      className="p-2 rounded bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                      title="Clear All"
                      aria-label="Clear all generated strings"
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>{strings.length} strings generated</span>
                  <span>Type: {settings.stringType} • Length: {settings.length}</span>
                </div>
              </div>

              {/* String Results */}
              {strings.length > 0 ? (
                <div className={`
                  ${viewMode === 'grid' 
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' 
                    : 'space-y-2'
                  }
                `}>
                  {strings.map((str) => (
                    <div
                      key={str.id}
                      className={`
                        bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow
                        ${viewMode === 'list' ? 'p-3' : 'p-4'}
                      `}
                    >
                      {viewMode === 'grid' ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className={`w-4 h-4 rounded flex items-center justify-center text-white ${getTypeColor(str.type)}`}>
                                {getTypeIcon(str.type)}
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                {str.type}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {str.length} chars
                            </span>
                          </div>
                          
                          <div className="relative">
                            <input
                              type={showStrings ? "text" : "password"}
                              value={str.value}
                              readOnly
                              className="w-full p-2 text-sm font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none"
                              aria-label="Generated random string"
                            />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                U:{str.uniqueness}%
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                R:{str.readability}%
                              </span>
                            </div>
                            <button
                              onClick={() => copyString(str.value, str.id)}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              aria-label="Copy string to clipboard"
                            >
                              {copiedId === str.id ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 flex items-center space-x-3">
                            <div className={`w-4 h-4 rounded flex items-center justify-center text-white flex-shrink-0 ${getTypeColor(str.type)}`}>
                              {getTypeIcon(str.type)}
                            </div>
                            <input
                              type={showStrings ? "text" : "password"}
                              value={str.value}
                              readOnly
                              className="flex-1 p-2 text-sm font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none"
                              aria-label="Generated random string"
                            />
                          </div>
                          <div className="ml-4 flex items-center space-x-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {str.length}ch
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {str.uniqueness}%
                            </span>
                            <button
                              onClick={() => copyString(str.value, str.id)}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              aria-label="Copy string to clipboard"
                            >
                              {copiedId === str.id ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 text-center">
                  <Hash className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No strings generated yet
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Generate random strings for API keys, tokens, UUIDs and more
                  </p>
                  <button
                    onClick={generateStringBatch}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
                    aria-label="Generate first batch of strings"
                  >
                    Generate Strings
                  </button>
                </div>
              )}
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    String Settings
                  </h2>
                  
                  <div className="space-y-4">
                    {/* String Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        String Type
                      </label>
                      <select
                        value={settings.stringType}
                        onChange={(e) => setSettings(prev => ({ ...prev, stringType: e.target.value as StringType }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                        aria-label="Select string type"
                      >
                        <option value="alphanumeric">Alphanumeric</option>
                        <option value="alphabetic">Alphabetic Only</option>
                        <option value="numeric">Numeric Only</option>
                        <option value="hexadecimal">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="uuid">UUID</option>
                        <option value="apikey">API Key</option>
                        <option value="token">Token</option>
                        <option value="binary">Binary</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>

                    {/* Length Control */}
                    {settings.stringType !== 'uuid' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Length: {settings.length}
                        </label>
                        <input
                          type="range"
                          min="4"
                          max="128"
                          value={settings.length}
                          onChange={(e) => setSettings(prev => ({ ...prev, length: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          aria-label="Adjust string length"
                        />
                      </div>
                    )}

                    {/* Case Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Case Format
                      </label>
                      <select
                        value={settings.caseFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, caseFormat: e.target.value as CaseFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                        aria-label="Select case format"
                      >
                        <option value="mixed">Mixed Case</option>
                        <option value="uppercase">UPPERCASE</option>
                        <option value="lowercase">lowercase</option>
                        <option value="title">Title Case</option>
                        <option value="camel">camelCase</option>
                        <option value="snake">snake_case</option>
                      </select>
                    </div>

                    {/* Encoding */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Encoding
                      </label>
                      <select
                        value={settings.encoding}
                        onChange={(e) => setSettings(prev => ({ ...prev, encoding: e.target.value as EncodingType }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                        aria-label="Select encoding type"
                      >
                        <option value="ascii">ASCII</option>
                        <option value="utf8">UTF-8</option>
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="url">URL Encoded</option>
                        <option value="html">HTML Entities</option>
                      </select>
                    </div>

                    {/* Character Options for Custom Type */}
                    {settings.stringType === 'custom' && (
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Character Sets</h4>
                        {[
                          { key: 'includeUppercase', label: 'Uppercase (A-Z)' },
                          { key: 'includeLowercase', label: 'Lowercase (a-z)' },
                          { key: 'includeNumbers', label: 'Numbers (0-9)' },
                          { key: 'includeSymbols', label: 'Symbols (!@#$...)' },
                          { key: 'includeSpecialChars', label: 'Special (±§...)' }
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof StringGenerationSettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                              aria-label={`Toggle ${label}`}
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Advanced Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Advanced Options</h4>
                      
                      {/* Prefix/Suffix */}
                      <div>
                        <label className="flex items-center space-x-3 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={settings.usePrefix}
                            onChange={(e) => setSettings(prev => ({ ...prev, usePrefix: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            aria-label="Toggle prefix usage"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Use Prefix</span>
                        </label>
                        {settings.usePrefix && (
                          <input
                            type="text"
                            value={settings.prefix}
                            onChange={(e) => setSettings(prev => ({ ...prev, prefix: e.target.value }))}
                            placeholder="Enter prefix..."
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                            aria-label="Enter prefix text"
                          />
                        )}
                      </div>

                      <div>
                        <label className="flex items-center space-x-3 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={settings.useSuffix}
                            onChange={(e) => setSettings(prev => ({ ...prev, useSuffix: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            aria-label="Toggle suffix usage"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Use Suffix</span>
                        </label>
                        {settings.useSuffix && (
                          <input
                            type="text"
                            value={settings.suffix}
                            onChange={(e) => setSettings(prev => ({ ...prev, suffix: e.target.value }))}
                            placeholder="Enter suffix..."
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                            aria-label="Enter suffix text"
                          />
                        )}
                      </div>

                      {/* Delimiters */}
                      <div>
                        <label className="flex items-center space-x-3 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={settings.useDelimiters}
                            onChange={(e) => setSettings(prev => ({ ...prev, useDelimiters: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            aria-label="Toggle delimiter usage"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Use Delimiters</span>
                        </label>
                        {settings.useDelimiters && (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={settings.delimiter}
                              onChange={(e) => setSettings(prev => ({ ...prev, delimiter: e.target.value }))}
                              placeholder="Delimiter..."
                              className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                              aria-label="Enter delimiter character"
                            />
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Segment Length: {settings.segmentLength}
                              </label>
                              <input
                                type="range"
                                min="2"
                                max="16"
                                value={settings.segmentLength}
                                onChange={(e) => setSettings(prev => ({ ...prev, segmentLength: parseInt(e.target.value) }))}
                                className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                                aria-label="Adjust segment length for delimiters"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Exclude Similar */}
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.excludeSimilar}
                          onChange={(e) => setSettings(prev => ({ ...prev, excludeSimilar: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          aria-label="Toggle exclusion of similar characters"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Exclude Similar (il1Lo0O)</span>
                      </label>
                    </div>

                    {/* Custom Character Set */}
                    <div>
                      <label className="flex items-center space-x-3 cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={settings.useCustomCharset}
                          onChange={(e) => setSettings(prev => ({ ...prev, useCustomCharset: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          aria-label="Toggle custom character set"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Custom Character Set</span>
                      </label>
                      {settings.useCustomCharset && (
                        <textarea
                          value={settings.customCharset}
                          onChange={(e) => setSettings(prev => ({ ...prev, customCharset: e.target.value }))}
                          placeholder="Enter custom characters..."
                          className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white"
                          rows={3}
                          aria-label="Enter custom character set"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Analytics
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.totalGenerated}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Generated
                        </div>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {analytics.totalBatches}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Batches
                        </div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {analytics.averageLength}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Length
                        </div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          {analytics.uniquenessScore}%
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Uniqueness
                        </div>
                      </div>
                    </div>

                    {/* Type Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Type Distribution
                      </h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {Object.entries(analytics.typeDistribution)
                          .filter(([, count]) => count > 0)
                          .sort(([,a], [,b]) => b - a)
                          .map(([type, count]) => (
                          <div key={type} className="flex justify-between items-center text-sm">
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded ${getTypeColor(type as StringType)}`} />
                              <span className="text-gray-600 dark:text-gray-400 capitalize">{type}</span>
                            </div>
                            <span className="font-medium text-gray-800 dark:text-white">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Encoding Usage */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Encoding Usage
                      </h4>
                      <div className="space-y-1">
                        {Object.entries(analytics.encodingUsage)
                          .filter(([, count]) => count > 0)
                          .map(([encoding, count]) => (
                          <div key={encoding} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                            <span className="capitalize">{encoding}</span>
                            <span>{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent History */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Recent History
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {analytics.generationHistory.slice(0, 5).map((entry, index) => (
                          <div key={index} className="text-xs text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                            <div className="flex justify-between">
                              <span className="capitalize">{entry.type}</span>
                              <span>{entry.count} items</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {entry.timestamp.toLocaleTimeString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Usage Tips */}
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Info className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          Pro Tips
                        </span>
                      </div>
                      <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
                        <li>• Use API Key type for production tokens</li>
                        <li>• UUID format for unique identifiers</li>
                        <li>• Base64 for encoding binary data</li>
                        <li>• Hex strings for color codes</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Hidden SEO Content */}
          <div className="sr-only" aria-hidden="true">
            <h2>Professional Random String Generator for Developers</h2>
            <p>
              Generate random strings for any development need with our comprehensive string generator tool. 
              Perfect for creating API keys, authentication tokens, UUIDs, session identifiers, 
              and custom string formats for web development and system administration.
            </p>
            
            <h3>String Generation Types</h3>
            <ul>
              <li>API keys and authentication tokens for secure applications</li>
              <li>UUID generation for unique identifier requirements</li>
              <li>Hexadecimal strings for color codes and hash values</li>
              <li>Base64 encoded strings for data transmission</li>
              <li>Alphanumeric codes for user-friendly identifiers</li>
              <li>Binary strings for low-level programming needs</li>
              <li>Custom character sets for specialized requirements</li>
              <li>Formatted strings with delimiters and segments</li>
            </ul>
            
            <h3>Developer Features</h3>
            <ul>
              <li>Bulk generation up to 50 strings per batch</li>
              <li>Multiple encoding formats (ASCII, UTF-8, Hex, Base64, URL, HTML)</li>
              <li>Case formatting options (mixed, upper, lower, camel, snake)</li>
              <li>Prefix and suffix customization for branded identifiers</li>
              <li>Delimiter support for structured string formats</li>
              <li>Character exclusion for readability improvements</li>
              <li>Export functionality for integration workflows</li>
              <li>Real-time analytics for generation monitoring</li>
            </ul>
            
            <h3>Use Cases and Applications</h3>
            <ul>
              <li>API key generation for web services and microservices</li>
              <li>Session token creation for user authentication systems</li>
              <li>Database primary keys and unique identifiers</li>
              <li>Test data generation for development environments</li>
              <li>Configuration file values and environment variables</li>
              <li>Temporary passwords and one-time codes</li>
              <li>File naming conventions and version identifiers</li>
              <li>Cache keys and distributed system identifiers</li>
            </ul>
            
            <h3>Technical Specifications</h3>
            <p>
              Built with modern web technologies including React and TypeScript for 
              reliability and type safety. Uses Web Crypto API for cryptographically 
              secure random generation when available, with enhanced fallback methods. 
              All string generation occurs locally in your browser for maximum privacy.
            </p>
            
            <h3>Integration and Export</h3>
            <p>
              Generated strings can be exported in JSON format for easy integration 
              into development workflows. The tool supports bulk operations and 
              maintains detailed analytics for monitoring string generation patterns 
              and usage statistics.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RandomStringGenerator;